import { Injectable, Injector } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { LoggerService } from '../../../services/logger/logger.service';
import { DiscountCoupon } from '../models/discount-coupon';
import { DiscountService } from './discount.service';
import { Order } from '../../order/models/order';
import { PartnerStateService } from '../../partner/services/partner-state.service';


@Injectable()
export class DiscountStateService {
  private _discountCoupons = new Array<DiscountCoupon>();
  private _message: string;
  private _order: Order;

  constructor(
    private logger: LoggerService,
    private discountService: DiscountService,
    private partnerStateService: PartnerStateService,
  ) {
  }

  addDiscountCoupon(
    coupon: string,
    productIds: number[],
    prices: number[],
    quantities: number[],
    bookingDate: string,
    bookingFromTime: number,
    bookingToTime: number,
    order: Order,
  ): Observable<boolean> {

    return of(false).pipe(
      switchMap(_ => {
        this._message = '';
        const existDiscountCoupons = this.isDiscountCouponExist(coupon);
        if (existDiscountCoupons) {
          return of(false);
        }

        const coupons = [];
        if (this.discountCoupons) {
          for (const discountCoupon of this.discountCoupons) {
            if (discountCoupon.name) {
              coupons.push(discountCoupon.code);
            }
          }
        }

        this._order = order;
        coupons.push(coupon);

        if (this.partnerStateService.loggedInPartner == null) {
          return this.discountService.getGuestCouponDiscounts(
            coupons, productIds, prices, quantities, bookingDate, bookingFromTime, bookingToTime);
        }

        return this.discountService.getCouponDiscounts(
          coupons, productIds, prices, quantities, bookingDate, bookingFromTime, bookingToTime);
      }),
      map(
        (result: DiscountCoupon[]) => {
          if (result && result.length > 0) {
            this._message = '';
            this._discountCoupons = [];

            for (const res of result) {
              if (!res || res.amount === 0) {
                continue;
              }

              if (res.is_billwerk_coupon && order && order.orderLines && order.orderLines.length > 0) {
                const productQtyOverloadMessage = 'You have more than one item in your shopping cart, please change your selection';
                if (order.orderLines.length > 1) {
                  this._message = productQtyOverloadMessage;
                  continue;
                }
                let productQty = 0;
                for (const orderLine of order.orderLines) {
                  if (orderLine.quantity) {
                    productQty += orderLine.quantity;
                  }
                  if (productQty > 1) {
                    break;
                  }
                }

                if (productQty > 1) {
                  this._message = productQtyOverloadMessage;
                  continue;
                }

              }

              this._discountCoupons.push(res);

            }
            this._order = null;
            return true;
          }
          this._order = null;
          return false;
        }
      ),
    );
  }

  private _checkCouponsCompatibility(discountCoupons: DiscountCoupon[], newDiscountCoupons: DiscountCoupon[]): boolean {
    const notCombineCoupons = discountCoupons.filter(x => x && !x.combine);
    const newNotCombineCoupons = newDiscountCoupons.filter(x => x && !x.combine);

    if (notCombineCoupons.length > 1 || newNotCombineCoupons.length > 1) {
      return false;
    }

    if (notCombineCoupons.length === 0 && newNotCombineCoupons.length === 0) {
      return true;
    }

    if (notCombineCoupons.length === 1 && newNotCombineCoupons.length === 1) {
      return notCombineCoupons[0].name === newNotCombineCoupons[0].name;
    }

    if ((discountCoupons.length + newDiscountCoupons.length) > 1) {
      return false;
    }

    return true;
  }

  get discountCoupons(): DiscountCoupon[] {
    return this._discountCoupons;
  }

  get totalAmountDiscountCoupons(): number {
    return DiscountCoupon.getTotalDiscountCouponAmount(this._discountCoupons);
  }

  get message(): string {
    return this._message;
  }

  reset(): void {
    this._discountCoupons = new Array<DiscountCoupon>();
    this._message = '';
    this._order = null;
  }

  loadCoupons(coupons: DiscountCoupon[]): void {
    if (coupons == null || coupons.length === 0) {
      this._discountCoupons = new Array<DiscountCoupon>();
      return;
    }

    this._discountCoupons = coupons.map(x => x.clone());
  }

  deleteDiscountCoupon(discountCoupon: DiscountCoupon): void {
    const index = this._discountCoupons.indexOf(discountCoupon);
    if (index > -1) {
      this._discountCoupons.splice(index, 1);
    }
  }

  isDiscountCouponExist(coupon: string): boolean {
    const existDiscountCoupons = this.discountCoupons.find(x => x.code === coupon);
    return existDiscountCoupons ? true : false;
  }
}
